import { useCallback } from 'react';

import { AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import { WeekdayType } from 'pages/private/owner/AthleteDetail/components/Reservations/types';
import { UseInfiniteQueryResult, useInfiniteQuery } from 'react-query';
import { PaginatedAthleteEventList, UrlParams } from 'types';
import { client } from 'utils/api-client';
import { encodeUrlParams, urlParamsFromUrl } from 'utils/functions';

interface QueryKeyType {
  pageParam?: UrlParams;
}

interface IUseAthleteEvents {
  athlete?: string;
  events?: string[];
  athletePackage?: string;
  includeUncredited?: boolean;
  startDateRange?: string;
  endDateRange?: string;
  enabled?: boolean;
  hideWaitlist?: boolean;
  limit?: string;
  offset?: string;
  ordering?: string;
  type?: 'past' | 'upcoming';
  weekdays?: WeekdayType['day'][];
  isBulkAdd?: boolean;
  csvExport?: string;
  isReserved?: boolean;
  isCheckedIn?: boolean;
}

function useAthleteEvents({
  athlete,
  events,
  athletePackage,
  includeUncredited,
  startDateRange,
  endDateRange,
  enabled = true,
  hideWaitlist,
  limit = '20',
  offset = '0',
  ordering,
  type,
  weekdays,
  isBulkAdd,
  csvExport,
  isReserved,
  isCheckedIn,
}: IUseAthleteEvents): UseInfiniteQueryResult<AxiosResponse<PaginatedAthleteEventList>> {
  const fetchAthleteEvents = useCallback(
    ({ pageParam = { limit, offset } }: QueryKeyType): Promise<AxiosResponse<PaginatedAthleteEventList>> => {
      const params = pageParam;

      if (athlete) {
        params.athlete = athlete;
      }
      if (events && events.length > 0) {
        params.events = events.join(',');
      }
      if (athletePackage) {
        params.athlete_package = athletePackage;
      }
      if (includeUncredited) {
        params.include_uncredited = 'True';
      }
      if (startDateRange !== undefined) {
        params.start_date_range = startDateRange;
      }
      if (endDateRange !== undefined) {
        params.end_date_range = endDateRange;
      }
      if (hideWaitlist !== undefined) {
        params.hide_waitlist = hideWaitlist ? 'True' : 'False';
      }
      if (type !== undefined) {
        params.type = type;
      }
      if (ordering) {
        params.ordering = ordering.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
      }
      if (weekdays && weekdays.length > 0) {
        params.weekdays = weekdays.join(',');
      }
      if (isBulkAdd != null) {
        params.is_bulk_add = isBulkAdd ? 'True' : 'False';
      }
      if (isReserved !== undefined) {
        params.is_reserved = isReserved ? 'True' : 'False';
      }
      if (isCheckedIn !== undefined) {
        params.is_checked_in = isCheckedIn ? 'True' : 'False';
      }

      return client.get(`athlete-events/?${encodeUrlParams(params)}`);
    },
    [
      limit,
      offset,
      athlete,
      events,
      athletePackage,
      includeUncredited,
      startDateRange,
      endDateRange,
      hideWaitlist,
      ordering,
      weekdays,
      isBulkAdd,
      type,
      isReserved,
      isCheckedIn,
    ],
  );

  return useInfiniteQuery(
    [
      'athleteEvents',
      athlete,
      events,
      athletePackage,
      includeUncredited,
      startDateRange,
      endDateRange,
      enabled,
      hideWaitlist,
      ordering,
      type,
      weekdays,
      isBulkAdd,
      limit,
      offset,
      csvExport,
      isReserved,
      isCheckedIn,
    ],
    fetchAthleteEvents,
    {
      getNextPageParam: page => {
        return page.data.next ? urlParamsFromUrl(page.data.next) : undefined;
      },
      enabled: enabled,
      refetchOnWindowFocus: true,
    },
  );
}

export function useAthleteHasUncreditedReservations(athleteId: string | undefined): boolean {
  const today = dayjs(new Date()).format('YYYY-MM-DD');

  const { data: athleteEvents } = useAthleteEvents({
    athlete: athleteId,
    startDateRange: dayjs(today).format('YYYY-MM-DD'),
    limit: '100',
    offset: '0',
    hideWaitlist: false,
    type: 'upcoming',
    ordering: 'start_date',
    enabled: !!athleteId,
  });

  return athleteEvents?.pages[0]?.data?.results?.some(ae => !ae.isCredited) ?? false;
}

export default useAthleteEvents;
